/**
 * Application Entry Point
 * 
 * Sets up all providers and renders the app.
 * 
 * Provider order (outer to inner):
 * 1. React.StrictMode - Development checks
 * 2. QueryProvider - React Query for data fetching
 * 3. MUI ThemeProvider - Material UI theming
 * 4. GoogleOAuthProvider - Google Sign-In
 * 5. BrowserRouter - React Router
 * 6. AuthProvider - Authentication context
 * 
 * THEMING NOTE:
 * Brand colors are defined in src/index.css as CSS variables.
 * MUI theme uses rawColors from tokens.ts for compatibility.
 * To change brand colors, edit the CSS variables in index.css.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import './i18n';
import App from './App';
import { AuthProvider } from './contexts/AuthContext';
import { QueryProvider } from './providers';
import { rawColors } from './shared/styles/tokens';
import { setupAxiosInterceptors } from './config/axios.config';
import { env } from './config/runtimeEnv';
import './index.css';

// Setup axios interceptors for IP blocking detection
setupAxiosInterceptors();

// =============================================================================
// CONFIGURATION
// =============================================================================

/** Google OAuth Client ID from runtime env (window.__ENV__) or build-time env */
const GOOGLE_CLIENT_ID = env.GOOGLE_CLIENT_ID;

/**
 * MUI Theme - Uses rawColors from design tokens
 * 
 * NOTE: MUI requires hex values, so we use rawColors.
 * These should stay in sync with CSS variables in index.css.
 */
const theme = createTheme({
  palette: {
    primary: {
      main: rawColors.primary,
      light: rawColors.primaryLight,
      dark: rawColors.primaryDark,
      contrastText: '#ffffff',
    },
    secondary: {
      main: rawColors.accent,
      light: '#fad837',
      dark: rawColors.accentDark,
      contrastText: '#000000',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily:
      '"Source Sans Pro", "Assistant", "Almarai", "Inter", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: '0 10px 40px -10px rgba(0, 0, 0, 0.15)',
          border: '1px solid #e2e8f0',
        },
        listbox: {
          padding: '8px',
        },
        option: {
          borderRadius: 8,
          margin: '2px 0',
          '&:hover': {
            backgroundColor: '#f1f5f9',
          },
          '&[aria-selected="true"]': {
            backgroundColor: '#fef2f4',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
          },
        },
      },
    },
  },
});

// =============================================================================
// RENDER
// =============================================================================

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    {/* React Query - Data fetching & caching */}
    <QueryProvider>
      {/* MUI Theme Provider */}
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {/* Google OAuth - Sign-in */}
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
          {/* React Router - Navigation */}
          <BrowserRouter>
            {/* Auth Context - User session */}
            <AuthProvider>
              <App />
            </AuthProvider>
          </BrowserRouter>
        </GoogleOAuthProvider>
      </ThemeProvider>
    </QueryProvider>
  </React.StrictMode>
);
